import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AppPropertyType,
  AppSystemProperties,
} from '../appSystemProperties.service';
import { MonitoringProvider } from './contracts/monitoring-provider.interface';
import { MonitoringProviderFactory } from './monitoring-provider.factory';
import {
  MonitoringConfig,
  MonitoringUser,
  MonitoringUserType,
  parseMonitoringConfig,
} from './models/monitoring-config.model';

const MODULE = 'admin' as const;
const DEFAULT_CONFIG_AUCTION_ID = 'lic001';

const delay = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  private activeProvider: MonitoringProvider | null = null;
  private currentConfig: MonitoringConfig | null = null;
  private configSubscription: Subscription | null = null;
  private pendingUser: MonitoringUser = { userId: '', metadata: {} };
  
  constructor(
    private readonly appSystemProperties: AppSystemProperties,
    private readonly providerFactory: MonitoringProviderFactory
  ) {}

  async bootstrap(): Promise<void> {
    if (this.activeProvider) {
      return;
    }

    await this.loadMonitoringConfig();
  }

  identify(userId: string, type: MonitoringUserType): void {
    this.pendingUser = this.buildUser(userId, type);
    this.activeProvider?.identify(this.pendingUser);
  }

  async startNewSession(): Promise<void> {
    const maxRetryCount = 5;
    let retryCount = 0;

    while (retryCount <= maxRetryCount) {
      const sessionCreated = await this.tryStartNewSession();
      if (sessionCreated) {
        break;
      }

      retryCount += 1;
      await delay(500);
    }
  }

  trackEvent(name: string, payload?: Record<string, unknown>): void {
    if (!this.isActive()) {
      return;
    }

    this.activeProvider?.trackEvent(name, payload);
  }

  captureException(error: unknown, context?: Record<string, string>): void {
    if (!this.isActive()) {
      return;
    }

    this.activeProvider?.captureException(error, context);
  }

  isActive(): boolean {
    return !!this.activeProvider && !!this.currentConfig?.enabled;
  }

  private async loadMonitoringConfig(): Promise<void> {
    return new Promise((resolve) => {
      this.appSystemProperties
        .getDoc(DEFAULT_CONFIG_AUCTION_ID, AppPropertyType.MONITORING)
        .subscribe(async (raw) => {
          const config = parseMonitoringConfig(raw);
          if (!config) {
            console.warn('[MonitoringService] Monitoring not configured');
            resolve();
            return;
          }

          await this.applyConfig(config);
          this.subscribeToConfigChanges();
          resolve();
        });
    });
  }

  private async applyConfig(config: MonitoringConfig): Promise<void> {
    const requiresProviderSwitch =
      !this.activeProvider ||
      !this.currentConfig ||
      this.currentConfig.provider !== config.provider ||
      this.currentConfig.projectKey !== config.projectKey ||
      this.currentConfig.adminSupport !== config.adminSupport;

    if (requiresProviderSwitch) {
      await this.switchProvider(config);
      return;
    }

    if (this.currentConfig?.enabled !== config.enabled) {
      this.activeProvider?.setEnabled(config.enabled);
    }

    this.currentConfig = config;
  }

  private async switchProvider(config: MonitoringConfig): Promise<void> {
    if (this.activeProvider) {
      await this.activeProvider.destroy();
      this.activeProvider = null;
    }

    this.activeProvider = await this.providerFactory.create(config.provider);
    await this.activeProvider.initialize(config);
    this.currentConfig = config;

    if (this.pendingUser.userId) {
      this.activeProvider.identify(this.pendingUser);
    }
  }

  private subscribeToConfigChanges(): void {
    this.unsubscribeFromConfigChanges();

    this.configSubscription = this.appSystemProperties
      .watchDoc(DEFAULT_CONFIG_AUCTION_ID, AppPropertyType.MONITORING)
      .subscribe((raw) => {
        void this.onConfigChanged(raw);
      });
  }

  private async onConfigChanged(
    raw: Record<string, unknown>
  ): Promise<void> {
    const config = parseMonitoringConfig(raw);
    if (!config) {
      return;
    }

    await this.applyConfig(config);
  }

  private unsubscribeFromConfigChanges(): void {
    this.configSubscription?.unsubscribe();
    this.configSubscription = null;
  }

  private async tryStartNewSession(): Promise<boolean> {
    if (!this.activeProvider) {
      return false;
    }

    try {
      await this.activeProvider.startNewSession();
      this.pendingUser = { userId: '', metadata: {} };
      return true;
    } catch (error) {
      console.error('[MonitoringService] Failed to start new session', error);
      return false;
    }
  }

  private buildUser(userId: string, type: MonitoringUserType): MonitoringUser {
    return {
      userId,
      metadata: {
        type,
        module: MODULE,
      },
    };
  }
}
